import React from "react"

type LocalizedText = { en?: string; nl?: string } | string | undefined

export type MissiesBannerData = {
  eyebrow?: LocalizedText
  title?: LocalizedText
  subtitle?: LocalizedText
  media?: string
  ctaButton?: LocalizedText
  ctaLink?: string
  trialButton?: LocalizedText
  trialLink?: string
  secondaryText?: LocalizedText
  secondaryLink?: string
  bridgeText?: LocalizedText
}

const L = (val: LocalizedText, languageCode: string, fallback = ""): string => {
  if (!val) return fallback
  if (typeof val === "string") return val
  return (val as any)[languageCode] || val.en || fallback
}

type Props = {
  data?: MissiesBannerData
  languageCode: string
  // First section on the page (home, #415): straight top edge under the
  // header instead of tucking under a hero above it.
  atTop?: boolean
  // Shown in the media column instead of data.media (Missies page, #427:
  // the playable demo instead of the video).
  children?: React.ReactNode
}

const MissiesBanner: React.FC<Props> = ({ data, languageCode, atTop = false, children }) => {
  if (!data) return null

  const eyebrow = L(data.eyebrow, languageCode)
  const title = L(data.title, languageCode, "Play Techtok Missions")
  const subtitle = L(data.subtitle, languageCode)
  const ctaLabel = L(data.ctaButton, languageCode, "Play Now")
  const trialLabel = L(data.trialButton, languageCode)
  const secondaryText = L(data.secondaryText, languageCode)
  const bridgeText = L(data.bridgeText, languageCode)
  const media = data.media || ""
  const isVideo = /\.(mp4|webm|mov)$/i.test(media)

  return (
    <section className={atTop ? "missies-banner missies-banner--top" : "missies-banner"}>
      <div className="missies-banner-inner">
        <div className="missies-banner-copy">
          {eyebrow && <span className="missies-banner-eyebrow">{eyebrow}</span>}
          <h1 className="missies-banner-title">{title}</h1>
          {subtitle && <p className="missies-banner-subtitle">{subtitle}</p>}

          <div className="missies-banner-actions">
            <a className="missies-banner-cta" href={data.ctaLink || "/games/missies/"}>
              {ctaLabel}
            </a>
            {trialLabel && data.trialLink && (
              <a className="missies-banner-cta missies-banner-cta--ghost" href={data.trialLink}>
                {trialLabel}
              </a>
            )}
            {secondaryText && data.secondaryLink && (
              <a className="missies-banner-secondary" href={data.secondaryLink}>
                {secondaryText} →
              </a>
            )}
          </div>

          {bridgeText && <p className="missies-banner-bridge">{bridgeText}</p>}
        </div>

        {children ? (
          <div className="missies-banner-media">{children}</div>
        ) : media && (
          <div className="missies-banner-media">
            {isVideo ? (
              <video autoPlay muted loop playsInline src={media} />
            ) : (
              <img src={media} alt="Techtok Missies" />
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export default MissiesBanner
